import { useLocalObservable } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import { useEffect } from 'react';
import type { PaysReportItem } from '@/models/report/types';
import type { PayType, PayStatus } from '@/models/pay/types';
import { fetchPaysReport } from '@/models/report/service';
import { fetchPayTypes, fetchPayStatuses } from '@/models/pay/service';
import type { Country } from '@/models/country/types';
import { lastMonthToToday } from '@/lib/dateUtils';
import { parseNumber } from '@/lib/format';
import { currencySign, groupByCountry } from '@/lib/currency';
import { isProcessedPayment } from '@/lib/paymentStatus';
import { countryStore } from '@/stores/country.store';

export function usePaysReportController() {
  const dates = lastMonthToToday();
  const store = useLocalObservable(() => ({
    dateStart: dates.start,
    dateEnd: dates.end,
    idPayType: '',
    dogNumber: '',
    idStatuses: '',
    items: [] as PaysReportItem[],
    payTypes: [] as PayType[],
    payStatuses: [] as PayStatus[],
    isLoading: false,
    error: null as string | null,
  }));

  async function load() {
    runInAction(() => {
      store.isLoading = true;
      store.error = null;
    });
    try {
      const items = await fetchPaysReport({
        date_start: store.dateStart,
        date_end: store.dateEnd,
        id_pay_type: store.idPayType || undefined,
        dog_number: store.dogNumber || undefined,
        id_statuses: store.idStatuses || undefined,
      });
      runInAction(() => {
        store.items = items;
      });
    } catch (e) {
      runInAction(() => {
        store.error = (e as Error).message;
      });
    } finally {
      runInAction(() => {
        store.isLoading = false;
      });
    }
  }

  useEffect(() => {
    void Promise.all([
      fetchPayTypes().then((items) =>
        runInAction(() => {
          store.payTypes = items;
        })
      ),
      fetchPayStatuses().then((items) =>
        runInAction(() => {
          store.payStatuses = items;
        })
      ),
    ]);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
  }, []);

  function set(
    field: 'dateStart' | 'dateEnd' | 'idPayType' | 'dogNumber' | 'idStatuses',
    value: string
  ) {
    runInAction(() => {
      store[field] = value;
    });
  }

  function byStatusChartData() {
    const map = new Map<string, { label: string; count: number; color: string }>();
    const colors = ['#3C91E6', '#9FD356', '#FA824C', '#a89fb0', '#6b6570'];
    let i = 0;
    for (const item of store.items) {
      const key = item.status_name || 'Неизвестно';
      if (!map.has(key)) {
        map.set(key, { label: key, count: 0, color: colors[i++ % colors.length] as string });
      }
      map.get(key)!.count++;
    }
    return Array.from(map.values());
  }

  // В строке отчёта страны нет — она берётся из типа оплаты (bepaid.by → Беларусь, tiptoppay.kz → Казахстан).
  function countryOf(item: PaysReportItem): Country | undefined {
    const countryName = store.payTypes.find((t) => t.type_name === item.pay_type_name)?.country;
    return countryName ? countryStore.byName.get(countryName) : undefined;
  }

  function currencyOf(item: PaysReportItem): string {
    return currencySign(countryOf(item));
  }

  function byDateChartData(items: PaysReportItem[]) {
    const map = new Map<string, { label: string; amount: number }>();
    for (const item of items) {
      const key = (item.date_pay || item.confirm_date || '').slice(0, 10);
      if (!key) continue;
      const prev = map.get(key) ?? { label: key, amount: 0 };
      map.set(key, { label: key, amount: prev.amount + parseNumber(item.money) });
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }

  const byCountry = groupByCountry(
    store.items.filter((i) => isProcessedPayment(i.status_name)),
    countryOf
  );
  const totals = byCountry.map((group) => ({
    ...group,
    amount: group.items.reduce((s, i) => s + parseNumber(i.money), 0),
  }));
  const byDateCharts = byCountry.map((group) => ({ ...group, data: byDateChartData(group.items) }));

  return { store, set, load, byStatusChartData, countryOf, currencyOf, totals, byDateCharts };
}
